import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../engine/design/primitives/Button';
import { Drawer } from '../../engine/design/primitives/Overlay';
import { EmptyState, Money, Stat } from '../../engine/design/primitives/Misc';
import { DayBars, Delta, Gauge, SegmentBar, SplitDonut } from '../../engine/design/primitives/Viz';
import { buildQueryString } from '../../engine/api/client';
import { formatDateTime, sumMinor, toMinorInt } from '../../lib/format';

// Home bands (docs/rtos-adoption-plan.md §4): the caller's week, work that has gone quiet, and
// receivables by age. A tile is a summary; clicking it opens the rows behind it in a side drawer
// (never navigating away), with a link to the full list. Counts come from the API as true totals;
// previews are capped there, so the number and the drawer never disagree.

export interface TimeBand {
  week_start: string;
  scheduled_today_minutes: number;
  scheduled_week_minutes: number;
  capacity_week_minutes: number | null;
  logged_week_minutes: number;
  billable_week_minutes: number;
  billable_target_minutes: number | null;
  days: { date: string; scheduled: number; billable: number; other: number }[];
  needs_time: null | { count: number; items: { id: string; subject: string | null; starts_at: string; ticket_id: string; ticket_ref: string | null; client_name: string | null }[] };
}
export interface AttentionBand {
  stale: { critical: number; serious: number; stale: number; items: { id: string; ref: string | null; summary: string | null; client_name: string | null; idle_days: number; sla_overdue: boolean }[] };
  clients: { count: number; items: { id: string; name: string; open: number; stale: number; overdue: number }[] };
}
export interface AgingRow { currency: string; current_minor: string; d30_minor: string; d60_minor: string; d90_minor: string; over90_minor: string; invoices: number }

/** `/tickets?filter[...]` — the list screens read the same DSL back from the URL. */
export function listLink(path: string, filter: { field: string; op: 'eq' | 'in' | 'is_null' | 'ne'; value: unknown }[], sort?: string): string {
  return `${path}${buildQueryString({ filter, ...(sort ? { sort } : {}) })}`;
}

const hours = (m: number) => `${(m / 60).toFixed(1)}h`;
const DAY = (iso: string) => new Date(`${iso}T00:00:00Z`).toLocaleDateString(undefined, { weekday: 'short', timeZone: 'UTC' }).slice(0, 2);

/** A tile that opens its rows in a drawer. */
export function DrillTile({ title, children, drill, to, toLabel = 'Open the full list' }: { title: string; children: ReactNode; drill: ReactNode; to?: string; toLabel?: string }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className="hf-home-tile hf-drill-tile" onClick={() => setOpen(true)} aria-haspopup="dialog" aria-label={title}>
        {children}
      </button>
      <Drawer open={open} onClose={() => setOpen(false)} title={title} footer={to ? <Link to={to} className="hf-btn ghost" onClick={() => setOpen(false)}>{toLabel} →</Link> : undefined}>
        {drill}
      </Drawer>
    </>
  );
}

function Rows({ children }: { children: ReactNode }) {
  return <div className="hf-stack" style={{ gap: 0 }}>{children}</div>;
}
function DrillRow({ to, primary, secondary, right }: { to: string; primary: ReactNode; secondary?: ReactNode; right?: ReactNode }) {
  return (
    <Link to={to} className="hf-row hf-drill-row">
      <span className="hf-grow" style={{ minWidth: 0 }}>
        <span style={{ display: 'block' }}>{primary}</span>
        {secondary && <span className="hf-muted" style={{ display: 'block', fontSize: 'var(--t-12)' }}>{secondary}</span>}
      </span>
      {right != null && <span className="hf-num hf-muted" style={{ fontSize: 'var(--t-12)', flexShrink: 0 }}>{right}</span>}
    </Link>
  );
}

const billableDays = (time: TimeBand) => time.days.map((d) => ({ key: d.date, label: DAY(d.date), value: d.billable, secondary: d.other }));

export function BookedTodayTile({ time }: { time: TimeBand }) {
  return (
    <DrillTile title="Booked today" drill={<DayBars days={time.days.map((d) => ({ key: d.date, label: DAY(d.date), value: d.scheduled }))} format={hours} />} to="/appointments" toLabel="Open the appointments">
      <Stat label="Booked today" icon="calendar" value={hours(time.scheduled_today_minutes)} sub={`${hours(time.scheduled_week_minutes)} this week${time.capacity_week_minutes ? ` · ${Math.round((time.scheduled_week_minutes / time.capacity_week_minutes) * 100)}% of capacity` : ''}`} />
    </DrillTile>
  );
}

export function BillableTile({ time }: { time: TimeBand }) {
  const target = time.billable_target_minutes;
  const toTarget = target != null ? time.billable_week_minutes - target : 0;
  return (
    <DrillTile title="Billable this week" drill={<DayBars days={billableDays(time)} format={hours} />} to="/timesheets" toLabel="Open the timesheets">
      <Stat
        label="Billable this week"
        icon="timer"
        value={hours(time.billable_week_minutes)}
        delta={target != null ? <Delta value={toTarget}>{toTarget >= 0 ? `${hours(toTarget)} over target` : `${hours(-toTarget)} to go`}</Delta> : 'No billable target set'}
        visual={target != null ? <Gauge value={time.billable_week_minutes} target={target} size={72} label={<>{Math.round((time.billable_week_minutes / Math.max(target, 1)) * 100)}%<small>of {hours(target)}</small></>} /> : undefined}
      />
    </DrillTile>
  );
}

export function LoggedTile({ time }: { time: TimeBand }) {
  return (
    <DrillTile title="Logged this week" drill={<DayBars days={billableDays(time)} format={hours} />} to="/timesheets" toLabel="Open the timesheets">
      <Stat
        label="Logged this week"
        icon="clock"
        value={hours(time.logged_week_minutes)}
        sub={`${hours(time.billable_week_minutes)} billable · ${hours(time.logged_week_minutes - time.billable_week_minutes)} other`}
        visual={<SplitDonut part={time.billable_week_minutes} whole={time.logged_week_minutes} size={72} centre={<>{time.logged_week_minutes > 0 ? Math.round((time.billable_week_minutes / time.logged_week_minutes) * 100) : 0}%<small>billable</small></>} />}
      />
    </DrillTile>
  );
}

export function NeedsTimeTile({ needs: n }: { time: TimeBand; needs: NonNullable<TimeBand['needs_time']> }) {
  return (
    <DrillTile
      title="Needs time"
      drill={n.items.length === 0 ? <EmptyState title="Every past session has time logged" icon="check" /> : (
        <Rows>
          {n.items.map((a) => (
            <DrillRow key={a.id} to={`/tickets/${a.ticket_id}`} primary={`${a.ticket_ref ?? 'Ticket'} · ${a.subject ?? 'Session'}`} secondary={a.client_name ?? undefined} right={formatDateTime(a.starts_at)} />
          ))}
        </Rows>
      )}
    >
      <Stat label="Needs time" icon="alert-circle" value={n.count} accent={n.count > 0} sub={n.count > 0 ? 'Past sessions with no time logged' : 'All sessions have time logged'} preview={n.items.slice(0, 2).map((a) => `${a.ticket_ref ?? ''} ${a.subject ?? ''}`.trim())} foot={n.count > 0 ? 'Log it ›' : undefined} />
    </DrillTile>
  );
}

export function GoneQuietTile({ attention }: { attention: AttentionBand }) {
  const s = attention.stale;
  const total = s.critical + s.serious + s.stale;
  return (
    <DrillTile
      title="Gone quiet"
      drill={s.items.length === 0 ? <EmptyState title="Nothing has gone quiet" hint="Your queue is current." icon="check" /> : (
        <Rows>
          {s.items.map((t) => (
            <DrillRow key={t.id} to={`/tickets/${t.id}`} primary={`${t.ref ?? 'Ticket'} · ${t.summary ?? ''}`} secondary={[t.client_name, t.sla_overdue ? '⚑ SLA overdue' : null].filter(Boolean).join(' · ')} right={`${t.idle_days}d`} />
          ))}
        </Rows>
      )}
      to={listLink('/tickets', [{ field: 'closed_at', op: 'is_null', value: true }], 'updated_at')}
      toLabel="Open the tickets"
    >
      <Stat
        label="Gone quiet"
        icon="hourglass"
        value={total}
        sub={total > 0 ? 'Open tickets with no activity for 3+ days' : 'Queue is current'}
        visual={total > 0 ? <SegmentBar segments={[{ key: 'c', label: '7d+ / SLA', value: s.critical, tone: 'neg' }, { key: 's', label: '5–6d', value: s.serious, tone: 'warn' }, { key: 'l', label: '3–4d', value: s.stale, tone: 'muted' }]} /> : undefined}
        foot={total > 0 ? 'Drive forward ›' : undefined}
      />
    </DrillTile>
  );
}

export function ClientsAttentionTile({ attention }: { attention: AttentionBand }) {
  const c = attention.clients;
  return (
    <DrillTile
      title="Clients needing attention"
      drill={c.items.length === 0 ? <EmptyState title="No client is waiting on you" icon="check" /> : (
        <Rows>
          {c.items.map((x) => (
            <DrillRow key={x.id} to={`/clients/${x.id}`} primary={x.name} secondary={`${x.open} open · ${x.stale} quiet${x.overdue ? ` · ⚑ ${x.overdue} overdue` : ''}`} />
          ))}
        </Rows>
      )}
      to="/clients"
      toLabel="Open the clients"
    >
      <Stat label="Clients needing attention" icon="users" value={c.count} sub={c.count > 0 ? 'With quiet or overdue tickets' : 'Every client is current'} preview={c.items.slice(0, 2).map((x) => `${x.name} · ${x.stale + x.overdue}`)} />
    </DrillTile>
  );
}

const BUCKETS: { key: keyof AgingRow; label: string; tone: 'muted' | 'warn' | 'neg' }[] = [
  { key: 'current_minor', label: 'Not due', tone: 'muted' },
  { key: 'd30_minor', label: '1–30d', tone: 'warn' },
  { key: 'd60_minor', label: '31–60d', tone: 'warn' },
  { key: 'd90_minor', label: '61–90d', tone: 'neg' },
  { key: 'over90_minor', label: '90d+', tone: 'neg' },
];

export function AgingTile({ aging }: { aging: AgingRow[] }) {
  const a = aging[0];
  if (!a) return null;
  const pastDue = sumMinor(BUCKETS.slice(1), (b) => a[b.key] as string);
  return (
    <DrillTile
      title="Receivables by age"
      drill={
        <div className="hf-stack">
          {aging.map((row) => (
            <section key={row.currency} className="hf-stack" style={{ gap: 'var(--s-2)' }}>
              <div className="hf-caps">{row.currency} · {row.invoices} invoice{row.invoices === 1 ? '' : 's'}</div>
              {BUCKETS.map((b) => (
                <div key={b.key} className="hf-between"><span>{b.label}</span><Money minor={row[b.key] as string} currency={row.currency} /></div>
              ))}
            </section>
          ))}
        </div>
      }
      to={listLink('/invoices', [{ field: 'status', op: 'in', value: ['issued', 'frozen'] }])}
      toLabel="Open unpaid invoices"
    >
      <Stat
        label="Past due"
        icon="receipt"
        value={<Money minor={pastDue} currency={a.currency} />}
        accent={pastDue > 0}
        sub={<span><Money minor={a.current_minor} currency={a.currency} /> not yet due · {a.invoices} unpaid invoice{a.invoices === 1 ? '' : 's'}</span>}
        visual={<SegmentBar segments={BUCKETS.map((b) => ({ key: b.key, label: b.label, value: toMinorInt(a[b.key] as string), tone: b.tone }))} format={(s) => <Money minor={a[s.key as keyof AgingRow] as string} currency={a.currency} />} />}
        foot={pastDue > 0 ? 'Chase AR ›' : undefined}
      />
    </DrillTile>
  );
}

/** "As of 10:42 · Refresh": when the numbers were folded, and a way to fold them again. */
export function Freshness({ at, onRefresh, busy, children }: { at: string; onRefresh: () => void; busy?: boolean; children?: ReactNode }) {
  return (
    <div className="hf-row hf-home-freshness" aria-live="polite">
      <span className="hf-muted">As of {new Date(at).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}</span>
      <Button size="sm" variant="ghost" icon="refresh-cw" onClick={onRefresh} busy={busy}>Refresh</Button>
      {children}
    </div>
  );
}
